import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import Icons from "../components/icons";
import loginbg from "../assets/loginbg.png";
import "./styles/OtpPage.css";

const OtpPage = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(30);
  const inputsRef = useRef([]);

  const {
    otpVerified,
    clearSuccess,
    clearErrors,
    confirmOtp,
    sendOtp, // ✅ Consolidated into a single useAuth call
    isOtpLoading,
    isForgotLoading, // This tracks our sendOtp request status
    otpError,
  } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const handleResendOtp = async () => {
    if (!email || isForgotLoading) return; // Guard clause against duplicate clicks

    const result = await sendOtp(email);
    if (result.meta.requestStatus === "fulfilled") {
      setTimeLeft(30);
      setOtp(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    } else {
      alert(result.payload || "Failed to resend OTP");
    }
  };

  useEffect(() => {
    if (!email) {
      navigate("/reset-password");
    }
  }, [email, navigate]);

  // ─────────────────────────────────────────
  // Countdown timer for resend
  // ─────────────────────────────────────────
  useEffect(() => {
    if (timeLeft === 0) return;
    const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  // ─────────────────────────────────────────
  // EFFECT: Navigate when OTP is verified
  // ─────────────────────────────────────────
  useEffect(() => {
    if (otpVerified) {
      clearSuccess();
      navigate("/reset-password", { state: { email } });
    }
  }, [otpVerified]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear errors on unmount
  useEffect(() => {
    return () => clearErrors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────
  // OTP input handlers
  // ─────────────────────────────────────────
  const handleChange = (val, i) => {
    if (!/^[0-9]?$/.test(val)) return;
    const newOtp = [...otp];
    newOtp[i] = val;
    setOtp(newOtp);
    if (val && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const handleKeyDown = (e, i) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    }
  };

  const handleVerify = () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length < 6) return;
    confirmOtp(email, enteredOtp);
  };

  const formatTime = () => `00:${timeLeft < 10 ? "0" : ""}${timeLeft}`;

  const isLoading = isOtpLoading || isForgotLoading;

  return (
    <div className="otp-wrapper bg-white">
      {/* LEFT */}
      <div className="otp-left">
        <img src={loginbg} alt="bg" />
        <p>
          Unleash Your Team's Potential With QwickHub <br />
          Built For Modern Product Teams
        </p>
      </div>

      {/* RIGHT */}
      <div className="otp-right ">
        {/* LOGO */}
        <div className="absolute top-4 right-6 flex items-center gap-0 cursor-pointer">
          <Icons.Logo />
          <span className="text-xl font-semibold">QwickHub</span>
        </div>

        <div className="otp-card">
          <h2 className="title text-3xl font-semibold p-5">Enter Your OTP</h2>

          <p className="desc">
            A Code Has Been Sent To Your Email. <br />
            Please Enter The 6-Digit Code Below To Secure Your Account
          </p>

          {/* Backend error — replaces alert() */}
          {otpError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{otpError}</p>
            </div>
          )}

          {/* OTP inputs */}
          <div className="otp-inputs bg-white">
            {otp.map((d, i) => (
              <input
                key={`otp-${i}`}
                maxLength="1"
                value={d}
                ref={(el) => (inputsRef.current[i] = el)}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                aria-label={`OTP digit ${i + 1}`}
                inputMode="numeric"
                disabled={isLoading}
              />
            ))}
          </div>

          {/* Resend Action Container */}
          <div className="resend flex items-center justify-center gap-2 mt-4 min-h-[24px]">
            {isForgotLoading ? (
              <button
                disabled
                className="resend-btn flex items-center gap-2 opacity-60 cursor-not-allowed transition-all duration-200"
              >
                {/* Clean, minimalist inline SVG spinner */}
                <svg
                  className="animate-spin h-4 w-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Sending...</span>
              </button>
            ) : timeLeft > 0 ? (
              <p className="m-0 text-sm text-gray-500">
                <span>Resend Code In</span> {formatTime()}
              </p>
            ) : (
              <button
                onClick={handleResendOtp}
                className="resend-btn transition-all duration-200 hover:opacity-80"
              >
                Resend OTP
              </button>
            )}
          </div>

          {/* Verify button */}
          <button
            className="verify-btn"
            onClick={handleVerify}
            disabled={isLoading || otp.join("").length < 6}
          >
            {isOtpLoading ? "Verifying..." : "Verify"}
          </button>
        </div>
      </div>
    </div>
  );
};

OtpPage.propTypes = {
  onVerify: PropTypes.func,
};

export default OtpPage;
